import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GROUP_DETAIL_QUERY_KEY } from './useGetGroupNegotiation';
import { QUERY_KEY } from './useGetGroupNegotiations';

export type ApiRequest_Join_Group_Negotiation = {
  communityId: number;
  negotiationId: number;
};

export type ApiResponse_Join_Group_Negotiation = {
  id: number;
  negotiation_id: number;
  member_id: number;
  joined_at: string;
  left_at?: string;
  status: string;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/group-negotiations/{negotiationId}/join';

export const useJoinGroupNegotiation = (
  stateFns?: MutationStateFns<ApiResponse_Join_Group_Negotiation>,
  options?: {
    navigateToGroup?: (communityId: number, negotiationId: number) => void;
  }
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Join_Group_Negotiation) =>
      handleApiCall<ApiResponse_Join_Group_Negotiation, Record<string, never>>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          communityId: params.communityId.toString(),
          negotiationId: params.negotiationId.toString(),
        }),
        {}
      ),
    onSuccess: (data, variables) => {
      // Invalidate both the list and individual group queries
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({ communityId: variables.communityId }),
      });
      queryClient.invalidateQueries({
        queryKey: GROUP_DETAIL_QUERY_KEY({ 
          communityId: variables.communityId, 
          negotiationId: variables.negotiationId 
        }),
      });
      enqueueSnackbar('Successfully joined save together group!', { variant: 'success' });
      
      // Auto-navigate to the group if navigation function is provided
      options?.navigateToGroup?.(variables.communityId, variables.negotiationId);
      
      stateFns?.onSuccess?.(data);
    },
    onError: error => {
      enqueueSnackbar('Failed to join save together group.', { variant: 'error' });
      stateFns?.onError?.(error as AxiosError);
    },
  });
};